/** GxPriceExplained: the share price in one sentence and a small table of what is in it (10.1). Own bank only; reads a stored `ValuationBreakdown`. */
import type { ValuationBreakdown } from "@bsim/domain";
import { GxDataTable, type TableRow } from "../financials/DataTable";
import { usePreviewContext } from "../shell/PreviewProvider";
import { GxCard } from "../theme/primitives";
import { priceExplained } from "./price";

export function GxPriceExplained({ valuation, sharesOutstanding, title = "Why the share price is what it is", asOf, testId = "price.explained", sentence = true, bare = false }: {
  /** Just the table, inside another card (Results opens it under the share-price chart). */ readonly bare?: boolean;
  /** False where the sentence already sits above (the results header). */ readonly sentence?: boolean;
  readonly valuation: ValuationBreakdown; readonly sharesOutstanding?: number | undefined; readonly title?: string; readonly asOf?: string; readonly testId?: string;
}) {
  const priceModel = usePreviewContext()?.params.valuation.priceModel;
  const explained = priceExplained(valuation, { sharesOutstanding, gapClosedPerQuarterRate: priceModel?.gapClosedPerQuarterRate, floorPtbv: priceModel?.floorPtbv });
  if (explained === null) return null;
  const rows: TableRow[] = explained.rows.map((r) => ({ id: r.id, label: r.label, cells: [r.value, r.note ?? null] }));
  if (bare) return <GxDataTable testId={`${testId}.table`} caption="What is in the share price" firstColumn="Part" columns={["Reading", "Note"]} rows={rows} dense />;
  return (
    <GxCard title={title} aside={asOf === undefined ? undefined : <span className="gx-small gx-faint">{asOf}</span>} testId={testId}>
      <div className="gx-card-b">
        {sentence ? <p className="gx-narr gx-num" data-testid={`${testId}.sentence`}>{explained.sentence}</p> : null}
        <GxDataTable testId={`${testId}.table`} caption="What is in the share price" firstColumn="Part" columns={["Reading", "Note"]} rows={rows} dense />
      </div>
    </GxCard>
  );
}
